import React, { useState, useEffect, useContext } from 'react'
import dynamic from 'next/dynamic'
import styled, { css } from 'styled-components'
import Link from 'next/link'
import { differenceInDays, startOfDay } from 'date-fns'
import { FaTrophy, FaTimesCircle } from 'react-icons/fa'
import Router, { useRouter } from 'next/router'
import { Button, Chip } from "@heroui/react"
import Error from 'next/error'

import Layout from '../../../components/layout'
import ContentArea from '../../../components/content-area'
import Header from '../../../components/header'
import Footer from '../../../components/footer'
import GroupDivider from '../../../components/group-divider'
import Stat from '../../../components/stat'
import ChallengeDetails from '../../../components/challenge-details'
import ErrorCard from '../../../components/error-card'
import ChallengeProgress from '../../../components/progress'
import ChallengeHeader from '../../../components/challenge-header'
import BackfillFlag from '../../../components/backfill-flag'
import { formatDate } from '../../../src/formatter'

const Participants = dynamic(() => import('../../../components/participants'))
const ResultsRace = dynamic(() => import('../../../components/results-race'))
const ResultsStandard = dynamic(() => import('../../../components/results-standard'))
const ParticipantMeta = dynamic(() => import('../../../components/participant-meta'))
const PointDetails = dynamic(() => import('../../../components/point-details'))

import { UserContext } from '../../../contexts/user-context'
import useChallengeStatus from '../../../hooks/use-challenge-status'
import useHasMounted from '../../../hooks/use-has-mounted'

const TeamInvite = styled.div`
  margin-top: 10px;
  text-align: center;
  font-size: 18px;
  > a {
    color: ${props => props.theme.primary};
    &:hover {
      color: #999;
    }
  }
`

const Challenge = ({ challenge, error }) => {
  const hasMounted = useHasMounted()
  const userHook = useContext(UserContext)
  const user = userHook.user
  
  const challengeStatusHook = useChallengeStatus(user.token, challenge.id)
  const challengeStatus = challengeStatusHook.challenge

  const [joined, setJoined] = useState() 
  const [showJoinMsg, setShowJoinMsg] = useState(false)
  const [showJoinWarningMsg, setShowJoinWarningMsg] = useState(false)
  
  // get id
  const router = useRouter()
  const { code } = router.query

  const replaced = challenge.group.type === 'team' ? challenge.challengeName.replace(/^the /i, '').replace(/ team$/i, '') : challenge.challengeName.replace(/^the /i, '').replace(/ challenge$/i, '')

  useEffect(() => {
    // check to see if current user has joined this challenge
    let tempJoined = false
    if (user && user.challengesJoined && user.challengesJoined.length) {
      tempJoined = user.challengesJoined.find(item => (item.id === challenge.id && item.joined))
      setJoined(tempJoined)
    }

    if ((!user && !challenge.showPublicly) || (user && !challenge.showPublicly && !tempJoined && user.organizationId !== challenge.group.organizationId)) {
      Router.push(`/join/[id]`, `/join/${challenge.id}`)
      return 
    }
  
    // show top green banner congratulating join
    if (window.joined && window.joined[challenge.id]) {
      setShowJoinMsg(true)
      window.joined[challenge.id] = undefined
    }
    // if owner and not joined, show warning that self is in fact not joined
    if (user.organizationId === challenge.group.organizationId && !tempJoined) {
      setShowJoinWarningMsg(true)
    }
  }, [])

  if (!challenge) return <Error statusCode={404} />

  return (
    <Layout
      title={`${challenge.challengeName} - (powered by Challenge Hound)`}
      description={challenge.challengeDesc}
      keywords={challenge.challengeDesc}
      image={challenge.logo} >
      <Header section='challenges' subsection='challenge' />
      <main className={challenge.theme}>
        <ContentArea>
          <GroupDivider group={challenge.group} />

          {error && <ErrorCard>{error}</ErrorCard>}
          
          {(hasMounted && user && user.id && joined && challenge.participantMeta && challenge.participantMeta.fields) && <ParticipantMeta user={user} userHook={userHook} challenge={challenge} />}
          
          {showJoinMsg && <div className='flex justify-center'>
            <Chip color='success' className='whitespace-normal pt-2 pb-14 mb-6 rounded-md items-start' classNames={{ closeButton: 'items-start' }} onClose={() => { setShowJoinMsg('') }}>
              <div className='font-bold text-large'>Congratulations!</div>
              You have joined the <span className='font-bold'>{replaced}</span> {challenge.group.type}!
              {challenge.backfill && <> If you have previous activities, allow ten minutes to sync.</>}
              {challenge.group.teamClone && <TeamInvite>
                <Link href={`/challenge/[id]/invite`} as={`/challenge/${challenge.id}/invite`}>Instructions to invite teammates</Link>
              </TeamInvite>}
            </Chip>
          </div>}

          {hasMounted && <ChallengeHeader challenge={challenge} desc page='challenge' />}

          {hasMounted && <>  
            <div className='md:grid md:grid-cols-2 mb-8'>
              <div className='mb-4 mb:mb-0'>
                <ChallengeDetails challenge={challenge} imperial={user.imperial} />
              </div>
              <div>
                {!Boolean(challenge.dayOfChallenge) ?
                  <Stat value={differenceInDays(new Date(challenge.start), startOfDay(new Date()))} label='Days Until Challenge Starts' />
                  : <div className='grid grid-cols-2 gap-2'>
                    <Stat value={challenge.dayOfChallenge} label='Days into Challenge' />
                    <Stat value={challenge.daysLeft} label='Days Remaining' />
                  </div>
                }
                {(Boolean(challenge.dayOfChallenge) && joined) && <div style={{ marginTop: '10px' }}>
                  {!Boolean(challenge.backfill) && <BackfillFlag joined={joined.joined} />}
                  {Boolean(challenge.backfill) && <div style={{ color: '#666', textAlign: 'center', fontSize: '14px' }}>You joined on {formatDate(joined.joined, true)}</div>}
                </div>}
              </div>
            </div>

            {challenge.measure === 'pointGoal' && <div className='mb-12 p-2 rounded-md'>
              <PointDetails challenge={challenge} />
            </div>}
            
            {!challenge.scheduled && <>
              {(joined && challengeStatus.teamResults && challengeStatus.teamResults.completed) &&
                <div className='flex items-center justify-center mb-10 mx-auto max-w-full md:max-w-[600px]'>
                  <FaTrophy className='text-secondary text-3xl md:text-5xl' />
                  <div className='text-2xl md:text-4xl text-center grow '>
                    Completed {formatDate(challengeStatus.teamResults.completed)}
                  </div>
                  <FaTrophy className='text-secondary text-3xl md:text-5xl' />
                </div>
              }
              {(joined && !challengeStatus.teamResults && challengeStatus.id && challengeStatus.userResults && challengeStatus.userResults.completed) &&
                <div className='flex items-center justify-center mb-10 mx-auto max-w-full md:max-w-[600px]'>
                  <FaTrophy className='text-secondary text-3xl md:text-5xl' />
                  <div className='text-2xl md:text-4xl text-center grow '>
                    Completed {formatDate(challengeStatus.userResults.completed)}
                  </div>
                  <FaTrophy className='text-secondary text-3xl md:text-5xl' />
                </div>
              }

              {showJoinWarningMsg && <div className='bg-warning rounded-md p-3 mb-5'>
                <div className='float-right ml-2 mb-2 cursor-pointer' onClick={() => { setShowJoinWarningMsg(false) }}><FaTimesCircle /></div>
                You have NOT joined the <strong>{replaced}</strong> {challenge.group.type}!
                Only participants that have <Link href={`/join/[id]`} as={`/join/${challenge.id}`}>joined</Link> the {challenge.group.type}, or have joined the group in other challenges, will be able to view this page. Since you are an admin, you can view this page.
              </div>}

              {((joined || challenge.isTeamChallenge) && challenge.measure && !challengeStatusHook.loading) &&
                <ChallengeProgress
                  challenge={challengeStatus}
                  user={user} />}

              {(challenge.measure && challenge.measure === 'race' && challengeStatus.id) && <ResultsRace challenge={challengeStatus} user={user} />}
            </>}

            {(challengeStatus.id && challenge.measure && challenge.measure !== 'race') && <ResultsStandard challenge={challengeStatus} user={user} />}

            {challenge.scheduled && <div>
              <h2 className='my-20 text-center '>This challenge has not started yet. When the challenge starts, this page will update with stats, graphs, and leader boards.</h2>
              {joined && <Participants challenge={challenge} user={user} />}
            </div>}
            
            {!joined && <div className='mt-12'>
              <div className='flex justify-center mb-20'>
                <Button 
                  color='primary'
                  size='lg'
                  onPress={() => { Router.push(`/join/[id]`, `/join/${challenge.id}`) }}>To Join Challenge Page</Button>
              </div>
              <Participants challenge={challenge} user={user} />
            </div>}
          </>}  
        </ContentArea>
      </main>
      <Footer />
    </Layout>
  );
}

export async function getServerSideProps(context) {
  const challengeId = context.query.id
  
  try {
    const getConfig = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    }
    const res = await fetch(`${process.env.API}/challenges/${challengeId}`, getConfig)
    if (!res.ok) {
      const error = await res.text()
      console.error(error)
      return { notFound: true }
    }
    
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=30, stale-while-revalidate=59'
    )
    
    const challenge = await res.json()
    return {
      props: {
        challenge
      }
    }
  } catch (error) {
    console.error(error)
    return { notFound: true }
  }
}

export default Challenge

